import { PlusOutlined, PrinterOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Dropdown, Flex, Form, Input, InputNumber, Modal, Radio, Space, Switch, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { NoWms, useWmsWarehouse } from "./common";
import { DeskList } from "./Desks";
import { printPdf } from "./packLabel";

type Packaging = Schemas["Packaging"];
type Values = Schemas["PackagingInput"];

/** Packing > Kemasan: boxes and mailers the desk suggests from (Plan §6.9). */
export function Packagings() {
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const { modal, message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const { current, loading } = useWmsWarehouse();
  const [editing, setEditing] = useState<Packaging | "new" | null>(null);
  const manage = can("warehouse.manage");
  const list = useQuery({
    queryKey: ["packagings", current?.id],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/packagings", { params: { path: { warehouseID: current!.id } } })),
    enabled: !!current,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/packagings/{packagingID}", { params: { path: { packagingID: id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["packagings"] }),
    onError: (err) => void message.error(errorText(err)),
  });

  const [printing, setPrinting] = useState(false);
  const printTags = async (paper: "thermal" | "a4") => {
    setPrinting(true);
    try {
      printPdf(
        await unwrap(
          api.GET("/v1/warehouses/{warehouseID}/packagings/labels", { params: { path: { warehouseID: current!.id }, query: { paper } }, parseAs: "blob" }),
        ),
      );
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      setPrinting(false);
    }
  };

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.pkg.title")}
        </Typography.Title>
        <Space wrap>
          <Dropdown
            disabled={(list.data?.items ?? []).every((p) => !p.active)}
            menu={{
              items: [
                { key: "thermal", label: t("wms.pkg.printThermal") },
                { key: "a4", label: t("wms.pkg.printA4") },
              ],
              onClick: ({ key }) => void printTags(key as "thermal" | "a4"),
            }}
          >
            <Button icon={<PrinterOutlined />} loading={printing}>
              {t("wms.pkg.printBarcodes")}
            </Button>
          </Dropdown>
          {manage && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
              {t("wms.pkg.add")}
            </Button>
          )}
        </Space>
      </Flex>
      <Typography.Paragraph type="secondary">{t("wms.pkg.help")}</Typography.Paragraph>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
      </Flex>
      <Table<Packaging>
        rowKey="id"
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={CLIENT_PAGING}
        locale={{ emptyText: t("wms.pkg.empty") }}
        columns={[
          {
            title: t("wms.pkg.code"),
            dataIndex: "code",
            width: 160,
            render: (v: string, p) => (
              <Space size={4}>
                <Typography.Text strong className="z-num">
                  {v}
                </Typography.Text>
                {!p.active && <Tag>{t("wms.pool.inactive")}</Tag>}
              </Space>
            ),
          },
          { title: t("common.name"), dataIndex: "name", render: (v: string) => v || "—" },
          { title: t("wms.pkg.kind"), dataIndex: "kind", width: 140, render: (v: Packaging["kind"]) => t(`wms.pkg.kind.${v}`) },
          {
            title: t("wms.pkg.inside"),
            width: 180,
            render: (_, p) => (
              <span className="z-num">
                {p.lengthMm} × {p.widthMm} × {p.heightMm}
              </span>
            ),
          },
          { title: t("wms.pkg.maxWeight"), dataIndex: "maxWeightG", width: 130, align: "right", render: (v: number | null) => (v ? `${formatNumber(v)} g` : "—") },
          { title: t("wms.pkg.ownWeight"), dataIndex: "weightG", width: 120, align: "right", render: (v: number) => `${formatNumber(v)} g` },
          ...(manage
            ? [
                {
                  key: "actions",
                  width: 150,
                  render: (_: unknown, p: Packaging) => (
                    <Space>
                      <Button size="small" onClick={() => setEditing(p)}>
                        {t("common.edit")}
                      </Button>
                      <Button
                        size="small"
                        danger
                        onClick={() =>
                          modal.confirm({
                            title: t("common.delete"),
                            content: t("wms.pkg.deleteConfirm", { code: p.code }),
                            okButtonProps: { danger: true },
                            okText: t("common.delete"),
                            cancelText: t("common.cancel"),
                            onOk: () => remove.mutateAsync(p.id),
                          })
                        }
                      >
                        {t("common.delete")}
                      </Button>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />
      {current && <PackagingModal target={editing} warehouseId={current.id} onClose={() => setEditing(null)} />}
      {current && <DeskList warehouseId={current.id} />}
    </>
  );
}

function PackagingModal({ target, warehouseId, onClose }: { target: Packaging | "new" | null; warehouseId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = { ...v, code: v.code.trim().toUpperCase(), maxWeightG: v.maxWeightG ?? null };
      return target === "new" || target === null
        ? unwrap(api.POST("/v1/warehouses/{warehouseID}/packagings", { params: { path: { warehouseID: warehouseId } }, body }))
        : unwrap(api.PUT("/v1/packagings/{packagingID}", { params: { path: { packagingID: target.id } }, body }));
    },
    onSuccess: () => {
      void message.success(t("wms.pkg.saved"));
      void queryClient.invalidateQueries({ queryKey: ["packagings"] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  const initial: Partial<Values> = target && target !== "new" ? target : { kind: "box", weightG: 0, active: true };
  return (
    <Modal
      open={target !== null}
      title={target === "new" ? t("wms.pkg.add") : t("wms.pkg.edit")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={initial} onFinish={(v) => save.mutate(v)}>
        <Flex gap={12}>
          <Form.Item name="code" label={t("wms.pkg.code")} rules={[{ required: true, message: t("field.required") }]} style={{ flex: 1 }}>
            <Input maxLength={32} placeholder="BOX-S" style={{ textTransform: "uppercase" }} />
          </Form.Item>
          <Form.Item name="name" label={t("common.name")} style={{ flex: 2 }}>
            <Input maxLength={100} />
          </Form.Item>
        </Flex>
        <Form.Item name="kind" label={t("wms.pkg.kind")}>
          <Radio.Group
            options={[
              { value: "box", label: t("wms.pkg.kind.box") },
              { value: "mailer", label: t("wms.pkg.kind.mailer") },
            ]}
          />
        </Form.Item>
        <Form.Item label={t("wms.pkg.inside")} extra={t("wms.pkg.insideHelp")} required>
          <Flex gap={8}>
            {(["lengthMm", "widthMm", "heightMm"] as const).map((f) => (
              <Form.Item key={f} name={f} noStyle rules={[{ required: true, message: t("field.required") }]}>
                <InputNumber min={1} max={5000} precision={0} placeholder={t(`catalog.${f}`)} style={{ width: "100%" }} />
              </Form.Item>
            ))}
          </Flex>
        </Form.Item>
        <Flex gap={12}>
          <Form.Item name="maxWeightG" label={t("wms.pkg.maxWeight")} extra={t("wms.pkg.maxWeightHelp")} style={{ flex: 1 }}>
            <InputNumber min={1} max={1_000_000} precision={0} suffix="g" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="weightG" label={t("wms.pkg.ownWeight")} style={{ flex: 1 }}>
            <InputNumber min={0} max={100_000} precision={0} suffix="g" style={{ width: "100%" }} />
          </Form.Item>
        </Flex>
        <Form.Item name="active" label={t("wms.pool.active")} valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
